<template>
    <div class="mainfont page-container">
        <v-snackbar v-model="showSnackBar" color="#0f172a" timeout="4000">
            {{ msg }}
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
                    <v-icon small color="white">mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
        <vue-element-loading :active="appLoading" :is-full-screen="true" background-color="#FFFFFF" color="#13736f"
            spinner="spinner" />

        <PageHeader title="Block Dates"
            subtitle="Reserve dates for this camp so applicants can no longer book them."
            icon="mdi-calendar-remove-outline">
            <template v-slot:actions>
                <v-btn depressed class="btn-act btn-act--neutral" style="height: 40px !important" @click="goBack">
                    <v-icon left small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
                <v-btn depressed class="btn-act btn-act--info" style="height: 40px !important" @click="viewBlocked">
                    <v-icon left small>mdi-calendar-search</v-icon>
                    Blocked dates
                </v-btn>
            </template>
        </PageHeader>

        <!-- Step 1: choose the period -->
        <section class="surface">
            <div class="surface-toolbar">
                <div>
                    <div class="surface-title">Select the period</div>
                    <div class="surface-subtitle">Check for approved bookings first, then block the dates.</div>
                </div>
            </div>
            <div class="bd-form">
                <div class="bd-field">
                    <label class="bd-label" for="bd-from">Block from *</label>
                    <DateField id="bd-from" v-model="startDate" outlined dense hide-details="auto"
                        background-color="white" :min="minDate" />
                </div>
                <div class="bd-field">
                    <label class="bd-label" for="bd-to">Block to *</label>
                    <DateField id="bd-to" v-model="endDate" outlined dense hide-details="auto"
                        background-color="white" :min="startDate || minDate" />
                </div>
            </div>
            <div class="bd-actions">
                <v-btn depressed class="btn-act btn-act--info" :loading="checking"
                    :disabled="!startDate || !endDate" @click="checkApprovedApplications">
                    <v-icon left small>mdi-magnify</v-icon>
                    Check approved applications
                </v-btn>
                <v-btn depressed class="btn-act btn-act--danger-solid"
                    :disabled="!startDate || !endDate || !approvedApplicationsChecked" @click="showConfirmationDialog">
                    <v-icon left small>mdi-calendar-lock-outline</v-icon>
                    Block date
                </v-btn>
            </div>
            <div v-if="!approvedApplicationsChecked && startDate && endDate" class="bd-hint">
                <v-icon x-small color="#94a3b8">mdi-information-outline</v-icon>
                Check the approved applications before blocking.
            </div>
        </section>

        <!-- Step 2: bookings that fall in the period -->
        <section v-if="approvedApplicationsChecked" class="surface bd-results">
            <div class="surface-toolbar">
                <div>
                    <div class="surface-title">
                        Approved applications in {{ formatDate(startDate) }} – {{ formatDate(endDate) }}
                    </div>
                    <div class="surface-subtitle">
                        {{ approvedApplications.length
                        }} {{ approvedApplications.length === 1 ? "application" : "applications" }} found
                    </div>
                </div>
            </div>

            <div v-if="approvedApplications.length > 0">
                <div class="bd-warning">
                    <v-icon small color="#b45309">mdi-alert-outline</v-icon>
                    <span>
                        Blocking these dates will <strong>cancel</strong> the
                        {{ approvedApplications.length === 1 ? "application" : "applications" }} below.
                    </span>
                </div>
                <div class="table-scroll">
                    <v-simple-table class="app-table">
                        <thead>
                            <tr>
                                <th class="text-left">#</th>
                                <th class="text-left">Applicant</th>
                                <th class="text-left">District</th>
                                <th class="text-left">Duration</th>
                                <th class="text-left">Members</th>
                                <th class="text-left">Requested date</th>
                                <th class="text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in approvedApplications" :key="item._id || i">
                                <td data-label="#">{{ i + 1 }}</td>
                                <td data-label="Applicant">
                                    <div class="cell-strong text-capitalize">{{ item.applicantname || "—" }}</div>
                                </td>
                                <td data-label="District">{{ item.district || "—" }}</td>
                                <td data-label="Duration">{{ item.campDuration || "—" }}</td>
                                <td data-label="Members">{{ item.memberCount || "—" }}</td>
                                <td data-label="Requested date" class="text-no-wrap">
                                    <v-icon small color="#13736f">mdi-calendar-blank-outline</v-icon>
                                    {{ formatDate(item.startDate) }}
                                </td>
                                <td data-label="Status">
                                    <span class="status-pill"
                                        :class="isApproved(item) ? 'status-pill--success' : 'status-pill--warning'">
                                        {{ isApproved(item) ? "Approved" : "Pending" }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>
            </div>

            <div v-else class="empty-state">
                <v-icon size="52">mdi-check-circle-outline</v-icon>
                <div class="cell-strong">No approved bookings in this period</div>
                <div class="cell-muted">You can block these dates without affecting any application.</div>
            </div>
        </section>

        <!-- Confirm -->
        <v-dialog v-model="confirmationDialog" max-width="520" persistent>
            <v-card class="dialog-card">
                <div class="dialog-head dialog-head--danger">
                    <div>
                        <div class="dialog-head-title">Block these dates?</div>
                        <div class="dialog-head-subtitle">Applicants will not be able to book them.</div>
                    </div>
                </div>
                <v-card-text class="bd-dialog-body">
                    <div class="bd-summary">
                        <div class="cell-strong">
                            <v-icon small color="#b91c1c">mdi-calendar-remove-outline</v-icon>
                            {{ formatDate(startDate) }} – {{ formatDate(endDate) }}
                        </div>
                        <div v-if="approvedApplications.length" class="cell-muted">
                            {{ approvedApplications.length }}
                            {{ approvedApplications.length === 1 ? "approved application will" : "approved applications will" }}
                            be cancelled.
                        </div>
                    </div>
                    <label class="bd-label" for="bd-remarks">Remarks <span class="bd-req">*</span></label>
                    <v-textarea id="bd-remarks" v-model="remarks" outlined dense rows="3" auto-grow counter="250"
                        maxlength="250" color="#13736f" background-color="white"
                        placeholder="Reason for blocking these dates" />
                </v-card-text>
                <v-card-actions class="px-5 pb-5">
                    <v-spacer />
                    <v-btn depressed class="btn-act btn-act--neutral" @click="closeConfirmation">Cancel</v-btn>
                    <v-btn depressed class="btn-act btn-act--danger-solid" :loading="blocking"
                        :disabled="!remarks.trim()" @click="blockDate">
                        <v-icon left small>mdi-calendar-lock-outline</v-icon>
                        Block dates
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader";

export default {
    components: { PageHeader },
    data() {
        return {
            id: this.$route.query.id,
            startDate: null,
            endDate: null,
            remarks: "",
            approvedApplications: [],
            approvedApplicationsChecked: false,
            confirmationDialog: false,
            appLoading: false,
            checking: false,
            blocking: false,
            msg: null,
            showSnackBar: false,
        };
    },
    computed: {
        minDate() {
            return moment().format("YYYY-MM-DD");
        },
    },
    watch: {
        // The check result belongs to one period; a new period needs a new check.
        startDate(val) {
            this.resetCheck();
            if (val && this.endDate && this.endDate < val) this.endDate = null;
        },
        endDate() {
            this.resetCheck();
        },
    },
    methods: {
        notify(text) {
            this.msg = text;
            this.showSnackBar = true;
        },
        errorText(error, fallback) {
            return (error && error.response && error.response.data && error.response.data.msg) || fallback;
        },
        resetCheck() {
            this.approvedApplicationsChecked = false;
            this.approvedApplications = [];
        },
        isApproved(item) {
            return item.divisonApprovalStatus === "Success" && item.superAdminApprovalStatus === "Success";
        },
        showConfirmationDialog() {
            this.remarks = "";
            this.confirmationDialog = true;
        },
        closeConfirmation() {
            this.confirmationDialog = false;
            this.remarks = "";
        },

        checkApprovedApplications() {
            this.checking = true;
            axios({
                method: "POST",
                url: "/divsion/blockdates",
                headers: { token: localStorage.getItem("token") },
                data: {
                    campid: this.id,
                    fromDate: this.startDate,
                    toDate: this.endDate,
                },
            })
                .then((response) => {
                    if (response.data.status) {
                        this.approvedApplications = response.data.data || [];
                        this.approvedApplicationsChecked = true;
                    }
                    this.notify(response.data.msg);
                })
                .catch((error) => {
                    this.notify(this.errorText(error, "Could not check the applications. Please try again."));
                })
                .finally(() => {
                    this.checking = false;
                });
        },

        blockDate() {
            if (!this.startDate || !this.endDate || !this.remarks.trim()) return;
            this.blocking = true;
            axios({
                method: "POST",
                url: "/divsion/blockdates",
                headers: { token: localStorage.getItem("token") },
                data: {
                    campid: this.id,
                    fromDate: this.startDate,
                    toDate: this.endDate,
                    status: true,
                    reason: this.remarks.trim(),
                },
            })
                .then((response) => {
                    this.notify(response.data.msg);
                    if (response.data.status) {
                        this.confirmationDialog = false;
                        this.remarks = "";
                        this.startDate = null;
                        this.endDate = null;
                        this.resetCheck();
                    }
                })
                .catch((error) => {
                    this.notify(this.errorText(error, "Could not block the dates. Please try again."));
                })
                .finally(() => {
                    this.blocking = false;
                });
        },

        goBack() {
            this.$router.go(-1);
        },
        viewBlocked() {
            this.$router.push({ path: "/ViewReservations", query: { id: this.id } });
        },
        formatDate(value) {
            return value ? moment.utc(value).format("DD MMM YYYY") : "—";
        },
    },
};
</script>

<style scoped>
.bd-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    padding: 20px;
}

.bd-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 700;
    color: #334155;
}

.bd-req {
    color: #dc2626;
}

.bd-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 20px;
}

.bd-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 20px 18px;
    font-size: 12.5px;
    color: #64748b;
}

.bd-results {
    margin-top: 20px;
}

.bd-warning {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 20px 4px;
    padding: 10px 14px;
    border-radius: 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    font-size: 13.5px;
    color: #92400e;
}

.bd-dialog-body {
    padding-top: 20px !important;
}

.bd-summary {
    margin-bottom: 18px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
}

@media (max-width: 600px) {
    .bd-form {
        grid-template-columns: 1fr;
        padding: 16px;
    }

    .bd-actions {
        flex-direction: column;
        padding: 0 16px 16px;
    }

    .bd-actions .v-btn {
        width: 100%;
    }

    .bd-hint {
        padding: 0 16px 16px;
    }

    .bd-warning {
        margin: 12px 16px 4px;
    }
}
</style>
